import { useCurrentUser } from 'hooks/useCurrentUser';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags';
import { useSlateSelector, useSlateStatic } from 'slate-react';
import { useImpressionGenerator } from 'hooks/useImpressionGenerator';
import { useCallback, useEffect, useRef } from 'react';
import { logger } from 'modules/logger';
import { telemetryContextFor } from 'modules/Apollo/types';
import { AutoImpressionRenderMethod } from 'domains/reporter/RichTextEditor/analytics/performance';
import { mostRecentInputStateFamily, RICH_TEXT_EDITOR } from 'hooks/useMostRecentInput';
import { useRecoilValue } from 'recoil';

export const AUTO_IMPRESSION_NAVIGATION_SETTLE_DELAY_MS = 300;

export const AutoGenerateImpressions = (): React.ReactElement | null => {
  const [isAutoImpressionsFFEnabled] = useFeatureFlagEnabled(FF.REPORTER_AUTO_IMPRESSIONS);
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const reporterSettings = data?.me?.reporterSettings;
  const isAutoImpressionEnabled = reporterSettings?.impressionGenerator?.enabled ?? false;

  if (!isAutoImpressionsFFEnabled || !isAutoImpressionEnabled) {
    return null;
  }

  return <WatchImpressions />;
};

export const WatchImpressions = (): React.ReactElement | null => {
  const editor = useSlateStatic();
  const { mostRecentInput } = useRecoilValue(mostRecentInputStateFamily(RICH_TEXT_EDITOR));
  const {
    isSelectionInImpressionSection,
    isImpressionContentEmpty,
    getImpressionContent,
    generateAndStitchImpression,
    isFirstNavigationToImpressionSection,
    setIsFirstNavigationToImpressionSection,
    isCurrentImpressionGenerated,
  } = useImpressionGenerator();
  const isInImpressionSection = useSlateSelector((editor) =>
    isSelectionInImpressionSection(editor, editor.selection ?? undefined)
  );
  const hasNavigatedIntoImpressionSection =
    useHasNavigatedIntoImpressionSection(isInImpressionSection);
  const { clearNavigationSettleTimer, scheduleNavigationSettleTimer } = useNavigationSettleTimer();

  useEffect(() => {
    // Keep a pending settle timer alive across unrelated re-renders.
    // Only cancel it when the generation preconditions become invalid.
    const canSettleNavigation =
      isInImpressionSection && isFirstNavigationToImpressionSection && mostRecentInput !== '';

    if (!canSettleNavigation) {
      clearNavigationSettleTimer();
      return;
    }

    if (hasNavigatedIntoImpressionSection && isFirstNavigationToImpressionSection) {
      scheduleNavigationSettleTimer(() => {
        const isStillInImpressionSection = isSelectionInImpressionSection(
          editor,
          editor.selection ?? undefined
        );

        if (!isStillInImpressionSection) {
          return;
        }

        setIsFirstNavigationToImpressionSection(false);
        const content = getImpressionContent(editor);
        if (isImpressionContentEmpty(content) || !isCurrentImpressionGenerated(editor)) {
          logger.info(
            `[AutoGenerateImpression] Impression generation triggered by navigation: ${isImpressionContentEmpty(content) ? 'content is empty' : ''}${!isCurrentImpressionGenerated(editor) ? 'content is not auto generated (ugc or default text)' : ''}`
          );
          generateAndStitchImpression(editor, true, {
            triggerMethod: AutoImpressionRenderMethod.Navigation,
          });
        }
      });
    }
  }, [
    clearNavigationSettleTimer,
    editor,
    generateAndStitchImpression,
    getImpressionContent,
    hasNavigatedIntoImpressionSection,
    isInImpressionSection,
    isCurrentImpressionGenerated,
    isFirstNavigationToImpressionSection,
    isImpressionContentEmpty,
    isSelectionInImpressionSection,
    setIsFirstNavigationToImpressionSection,
    mostRecentInput,
    scheduleNavigationSettleTimer,
  ]);

  return null;
};

AutoGenerateImpressions.displayName = 'AutoGenerateImpressions';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(AutoGenerateImpressions);

const useHasNavigatedIntoImpressionSection = (isInImpressionSection: boolean): boolean => {
  const wasInImpressionSectionRef = useRef<boolean | null>(null);
  const hasNavigatedIntoImpressionSection =
    wasInImpressionSectionRef.current === false && isInImpressionSection;

  useEffect(() => {
    wasInImpressionSectionRef.current = isInImpressionSection;
  }, [isInImpressionSection]);

  return hasNavigatedIntoImpressionSection;
};

const useNavigationSettleTimer = (): {
  clearNavigationSettleTimer: () => void;
  scheduleNavigationSettleTimer: (callback: () => void) => void;
} => {
  const navigationSettleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearNavigationSettleTimer = useCallback(() => {
    if (navigationSettleTimerRef.current != null) {
      clearTimeout(navigationSettleTimerRef.current);
      navigationSettleTimerRef.current = null;
    }
  }, []);

  const scheduleNavigationSettleTimer = useCallback((callback: () => void) => {
    if (navigationSettleTimerRef.current != null) {
      return;
    }

    navigationSettleTimerRef.current = setTimeout(() => {
      navigationSettleTimerRef.current = null;
      callback();
    }, AUTO_IMPRESSION_NAVIGATION_SETTLE_DELAY_MS);
  }, []);

  useEffect(
    () => () => {
      clearNavigationSettleTimer();
    },
    [clearNavigationSettleTimer]
  );

  return { clearNavigationSettleTimer, scheduleNavigationSettleTimer };
};
